import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  sectionMargins,
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  brandFonts,
  fontSize,
} from '../styles';
import { Container, Svg, Heading, Button } from './ui';
import GoogleMapReact from 'google-map-react';
import { formatPrice } from '../utils';
import mapMarkerIcon from '../images/map-marker-icon.svg';

const StyledLocationMap = styled.section`
  ${({ isSpaListing, isSpaLocationGroup }) => {
    if (!isSpaListing && !isSpaLocationGroup) {
      return css`
        ${sectionMargins()};
      `;
    }
  }}
`;

const StyledContainer = styled(Container)`
  ${({ isSpaListing, isSpaLocationGroup }) => {
    if (isSpaListing || isSpaLocationGroup) {
      return css`
        padding-right: 0;
        padding-left: 0;
      `;
    }
  }}
`;

const StyledHeader = styled.header`
  margin-bottom: 20px;

  ${minBreakpointQuery.small`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 30px;
  `}
`;

const StyledHeaderInner = styled.div`
  ${({ singleLocation }) => {
    if (singleLocation) {
      return css`
        ${minBreakpointQuery.mlarge`
          display: flex;
          justify-content: space-between;
          align-items: center;
        `}
      `;
    }
  }}
`;

const StyledHeading = styled(Heading)`
  text-align: center;
`;

const StyledDirections = styled.a`
  text-decoration: underline;

  ${({ inHeader }) => {
    if (inHeader) {
      return css`
        display: block;
        text-align: center;

        ${maxBreakpointQuery.mlarge`
          margin-top: 10px;
        `}
      `;
    } else {
      return css`
        display: inline-block;
        margin-top: 8px;
      `;
    }
  }}
`;

const StyledMap = styled.div`
  ${({ hasBackground }) => {
    if (hasBackground) {
      return css`
        position: relative;
        padding-bottom: 35px;

        ${minBreakpointQuery.small`
          padding-bottom: 55px;
        `}

        ${minBreakpointQuery.medium`
          padding-bottom: 75px;
        `}

        ${minBreakpointQuery.large`
          padding-bottom: 95px;
        `}

        &:before {
          content: '';
          position: absolute;
          top: 35px;
          right: 0;
          bottom: 0;
          width: calc(50% + 30px);
          background-color: ${brandColours.quaternary};

          ${minBreakpointQuery.small`
            top: 55px;
            width: calc(50% + 50px);
          `}

          ${minBreakpointQuery.medium`
            top: 75px;
            width: calc(50% + 70px);
          `}

          ${minBreakpointQuery.large`
            top: 95px;
            width: calc(50% + 90px);
          `}
        }
      `;
    }
  }}
`;

const StyledMapOuter = styled.div`
  position: relative;
`;

const StyledMapInner = styled.div`
  position: relative;
  max-width: 100%;
  overflow: hidden;

  ${({ singleLocation }) => {
    if (singleLocation) {
      return css`
        height: 360px;

        ${minBreakpointQuery.small`
          height: 390px;
        `}

        ${minBreakpointQuery.medium`
          height: 420px;
        `}

        ${minBreakpointQuery.large`
          height: 450px;
        `}
        
        .gmnoprint,
        .gm-style-cc {
          display: none;
        }
      `;
    } else {
      return css`
        height: 520px;

        ${minBreakpointQuery.small`
          height: 560px;
        `}

        ${minBreakpointQuery.large`
          height: 600px;
        `}
      `;
    }
  }}
`;

const StyledMarker = styled.div`
  position: relative;
  display: inline-block;
  transform: translate(-50%, -100%);
  text-align: center;
  z-index: ${({ display }) => (display ? 1 : '0')};

  &:hover {
    z-index: 1;
  }
`;

const StyledInfo = styled.div`
  display: ${({ display }) => (display ? 'block' : 'none')};
  background-color: ${standardColours.white};
  min-width: 225px;
  padding: 25px 20px;
  margin-bottom: 10px;
`;

const StyledName = styled.p`
  ${brandFonts.secondary}
  ${fontSize(21)}
  margin-bottom: 5px;
`;

const StyledLocationPrice = styled.p`
  ${brandFonts.primary}
  ${fontSize(14)}
`;

const StyledButton = styled(Button)`
  margin-top: 10px;
`;

const StyledMarkerIcon = styled.button`
  svg {
    width: 88px;
    fill: ${({ display }) =>
      display ? brandColours.tertiary : brandColours.primary};
  }
`;

const StyledMarkerPoint = styled.button`
  position: relative;
  display: inline-block;
  padding: 6px;
  background-color: ${brandColours.primary};
  color: ${standardColours.white};
  ${fontSize(14)};
  z-index: 1;

  &:hover {
    background-color: ${brandColours.tertiary};

    &:after {
      border-top: 10px solid ${brandColours.tertiary};
    }
  }

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    right: 0;
    margin: auto;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid ${brandColours.primary};
  }
`;

const StyledAddress = styled.address`
  padding: 25px;
  color: ${standardColours.white};
  font-style: inherit;
  background-color: ${brandColours.primary};

  ${minBreakpointQuery.small`
    position: absolute;
    bottom: 0;
    right: 0;
    padding: 25px;
    width: 200px;
  `}

  ${minBreakpointQuery.large`
    bottom: 30px;
    right: 30px;
  `}
`;

const StyledAddressHeading = styled.h3`
  ${fontSize(18)}
  ${brandFonts.secondary}
  margin-bottom: 10px;
`;

const StyledAddressText = styled.div`
  ${fontSize(14)}
  line-height: 1.4;
`;

const LocationMap = ({
  heading,
  locations = [],
  marker,
  mapZoom,
  mapToggleRatings,
  hasBackground,
  isSpaListing,
  isSpaLocationGroup,
}) => {
  const {
    spaseekers: {
      site: { googleMapsApiKey },
    },
  } = useStaticQuery(graphql`
    query LocationMapQuery {
      spaseekers {
        site {
          googleMapsApiKey
        }
      }
    }
  `);

  const [activeItem, setActiveItem] = useState(false);

  const singleLocation = locations.length === 1 ? locations[0] : false;

  const mapProps = {
    zoom: mapZoom || 8,
  };

  if (singleLocation) {
    mapProps.mapCenter = {
      lat: locations[0].location.latitude,
      lng: locations[0].location.longitude,
    };
  } else {
    const avgLatitude =
      locations.reduce(
        (previousValue, { location: { latitude } }) => previousValue + latitude,
        0
      ) / locations.length;
    const avgLongitude =
      locations.reduce(
        (previousValue, { location: { longitude } }) =>
          previousValue + longitude,
        0
      ) / locations.length;

    mapProps.mapCenter = {
      lat: avgLatitude,
      lng: avgLongitude,
    };
  }

  const Directions = ({ inHeader }) =>
    singleLocation &&
    singleLocation.nameWithLocation && (
      <StyledDirections
        href={`https://www.google.co.uk/maps/dir//${singleLocation.nameWithLocation}`}
        target="_blank"
        rel="noreferrer noopener"
        inHeader={inHeader}
      >
        Get Directions
      </StyledDirections>
    );

  return (
    <StyledLocationMap
      id="locations-map"
      isSpaListing={isSpaListing}
      isSpaLocationGroup={isSpaLocationGroup}
    >
      {heading && (
        <StyledHeader>
          <StyledContainer
            narrow={true}
            isSpaListing={isSpaListing}
            isSpaLocationGroup={isSpaLocationGroup}
          >
            <StyledHeaderInner singleLocation={singleLocation}>
              <StyledHeading alt={singleLocation}>{heading}</StyledHeading>
              <Directions inHeader={true} />
            </StyledHeaderInner>
          </StyledContainer>
        </StyledHeader>
      )}
      <StyledMap hasBackground={hasBackground}>
        <StyledContainer
          narrow={true}
          isSpaListing={isSpaListing}
          isSpaLocationGroup={isSpaLocationGroup}
        >
          <StyledMapOuter>
            <StyledMapInner singleLocation={singleLocation}>
              {locations && locations.length > 0 && (
                <GoogleMapReact
                  bootstrapURLKeys={{ key: googleMapsApiKey }}
                  defaultCenter={mapProps.mapCenter}
                  defaultZoom={mapProps.zoom}
                  options={{
                    disableDefaultUI: singleLocation ? true : false,
                  }}
                >
                  {locations.map(
                    (
                      {
                        name,
                        city,
                        county,
                        location: { latitude, longitude },
                        spaRating,
                        minPrice,
                        isPricePerPerson,
                        url,
                      },
                      i
                    ) => {
                      const display = activeItem === i;

                      if (singleLocation || marker === 'icon') {
                        return (
                          <StyledMarker
                            key={`map-marker-${i}`}
                            lat={latitude}
                            lng={longitude}
                            display={display}
                          >
                            {name && (
                              <StyledInfo display={display}>
                                <StyledName>{name}</StyledName>
                                {url && (
                                  <StyledButton
                                    alt={true}
                                    arrow={true}
                                    to={url}
                                  >
                                    View Spa
                                  </StyledButton>
                                )}
                              </StyledInfo>
                            )}
                            <StyledMarkerIcon
                              display={display}
                              onClick={() =>
                                setActiveItem(display ? undefined : i)
                              }
                            >
                              <Svg image={mapMarkerIcon} />
                            </StyledMarkerIcon>
                          </StyledMarker>
                        );
                      } else {
                        return (
                          <StyledMarker
                            key={`map-marker-${i}`}
                            lat={latitude}
                            lng={longitude}
                            display={display}
                          >
                            <StyledInfo display={display}>
                              <StyledName>{name}</StyledName>
                              <StyledLocationPrice>
                                {`${city}, ${county.name}`}
                                <br />
                                From {formatPrice(minPrice)} per{' '}
                                {isPricePerPerson ? 'person' : 'package'}
                              </StyledLocationPrice>
                              <StyledButton alt={true} arrow={true} to={url}>
                                View Spa
                              </StyledButton>
                            </StyledInfo>
                            <StyledMarkerPoint
                              onClick={() =>
                                setActiveItem(display ? undefined : i)
                              }
                            >
                              {mapToggleRatings
                                ? `${spaRating || '?'}/5`
                                : formatPrice(minPrice)}
                            </StyledMarkerPoint>
                          </StyledMarker>
                        );
                      }
                    }
                  )}
                </GoogleMapReact>
              )}
            </StyledMapInner>
            {singleLocation && (
              <StyledAddress>
                <StyledAddressHeading>
                  {singleLocation.name}
                </StyledAddressHeading>
                <StyledAddressText
                  dangerouslySetInnerHTML={{ __html: singleLocation.address }}
                />
                {isSpaLocationGroup && <Directions />}
              </StyledAddress>
            )}
          </StyledMapOuter>
        </StyledContainer>
      </StyledMap>
    </StyledLocationMap>
  );
};

export default LocationMap;
